<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, watch } from "vue"
import { useI18n } from "vue-i18n"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Modifier la Consommation Éligible CEE",
            label: "Conso. éligible",
            helper: "Correspond à la consommation annuelle éligible des Certificats d’économie d’énergie (CEE) (ex. 750 MWh/an).",
            placeholder: "750 kWh/an",
            cancel: "Retour",
            submit: "Valider",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    open: boolean // Contrôle la visibilité de la modal
    value: number | null // Valeur initiale de la consommation éligible
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<Props>()

const emit = defineEmits<{
    "update:open": [value: boolean] // Met à jour la visibilité de la modal
    submit: [value: number] // Émet la nouvelle valeur soumise
}>()

// ================================
// REACTIVE STATE
// ================================
// Valeur locale de la consommation éligible CEE
const modelValue = ref<number | null>(props.value)
const modelOpen = defineModel<boolean>("open")

// ================================
// WATCHERS
// ================================
// Synchronise la valeur locale avec la prop model
watch(
    () => props.value,
    (newModel) => {
        modelValue.value = newModel ?? null
    },
    { immediate: true } // Initialise immédiatement avec la valeur de la prop
)

// ================================
// METHODS
// ================================
/**
 * Ferme la modal en émettant l'événement de mise à jour
 */
function close(): void {
    modelOpen.value = false
}

/**
 * Soumet la valeur saisie si elle est valide
 * Ferme automatiquement la modal après soumission
 */
function onSubmit(): void {
    if (modelValue.value !== null) {
        emit("submit", modelValue.value)
        close()
    }
}
</script>

<template>
    <BaseDialog
        :visible="open"
        modal
        :draggable="false"
        :closable="true"
        :breakpoints="{ '575px': '90vw' }"
        :pt="{ header: { style: 'padding: 10px' } }"
        class="modal-edit-cee w-[600px] rounded-[14px]"
        :style="{ width: '400px' }"
        position="top"
        @update:visible="modelOpen = $event"
    >
        <!-- Header vide avec espacement -->
        <template #header>
            <div class="h-2"></div>
        </template>
        <!-- Contenu -->
        <div class="flex flex-col gap-10">
            <!-- Titre de la modal -->
            <h2 class="text-center text-lg font-bold text-primary">
                {{ t("title") }}
            </h2>

            <!-- Champ de saisie numérique -->
            <BaseInputWrapper
                :label="t('label')"
                suffix=" kWh/an"
                input-id="cee"
                unfloat
            >
                <template #label>
                    <i
                        class="pi pi-info-circle align-middle ml-2"
                        v-tooltip.top="t('helper')"
                        style="font-size: 0.8rem"
                    />
                </template>
                <BaseInputNumber
                    v-model="modelValue"
                    :use-grouping="false"
                    :min="0"
                    id="cee"
                    class="w-full"
                    :min-fraction-digits="2"
                    :max-fraction-digits="3"
            /></BaseInputWrapper>
        </div>

        <!-- Footer - boutons -->
        <template #footer>
            <div class="flex w-full justify-between gap-2">
                <BaseButton
                    :label="t('cancel')"
                    variant="text"
                    @click="close"
                />
                <BaseButton
                    :label="t('submit')"
                    :disabled="modelValue === null"
                    @click="onSubmit"
                />
            </div>
        </template>
    </BaseDialog>
</template>
